import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal, InjectionToken } from '@angular/core';
import { Auth, onAuthStateChanged, User } from 'firebase/auth';
import { injectFirebaseAuth } from '@open/ngx-firebase/auth';
import { LocalStorageService } from './local-storage.service';
import { BeneficiaryModel } from '@models/beneficiaries/Beneficiary.model';
import { OpenProductName } from '@models/products/Products.model';
import { getProductSubscriptionEntry } from '@assets/utils/beneficiaries/beneficiarySubscription.util';
import { SUPPORTED_BENEFICIARY_PRODUCTS } from './user.service';

/** `uid` is part of the payload and is verified on every read: `QR_CODE` / `QR_CODE_PRUNEO` already survive a change of account on a shared device, and without this guard beneficiary B could get beneficiary A's dashboard variant. */
export interface ProductContext {
  uid: string;
  products: OpenProductName[];
  activeProduct: OpenProductName;
  updatedAt: number;
}

/** Deliberately a tri-state, with `'unknown'` a first-class value: no `undefined` shortcut, since that would silently fall through to the OPEN2PAY branch. */
export type ProductResolution = ProductContext | 'unknown';

/** Lets the spec substitute a fake `Auth` without booting Firebase. */
export const PRODUCT_CONTEXT_AUTH: InjectionToken<Auth> = new InjectionToken<Auth>('PRODUCT_CONTEXT_AUTH', {
  providedIn: 'root',
  factory: (): Auth => injectFirebaseAuth(),
});

@Injectable({
  providedIn: 'root',
})
export class ProductContextService {
  /** `Auth` directly, not `AuthService`: `AuthService.logout()` calls `forget()` below, so depending on `AuthService` here would close a DI cycle. */
  #auth: Auth = inject(PRODUCT_CONTEXT_AUTH);
  #localStorage: LocalStorageService = inject(LocalStorageService);
  #destroyRef: DestroyRef = inject(DestroyRef);

  #resolution: WritableSignal<ProductResolution> = signal<ProductResolution>('unknown');
  resolution: Signal<ProductResolution> = this.#resolution.asReadonly();

  activeProduct: Signal<OpenProductName | undefined> = computed(() => {
    const resolution: ProductResolution = this.#resolution();
    return resolution === 'unknown' ? undefined : resolution.activeProduct;
  });

  constructor() {
    // The raw `onAuthStateChanged`, not the shared `user$` stream: the latter is zone-wrapped and needs a full `provideFirebaseApp` context, untestable without booting Firebase.
    const unsubscribe: () => void = onAuthStateChanged(this.#auth, (authUser: User | null) => {
      this.#resolution.set(authUser ? this.readFor(authUser.uid) : 'unknown');
    });

    this.#destroyRef.onDestroy(unsubscribe);
  }

  /** Called only from the online data path, the only place where the information is authoritative. */
  remember(beneficiary: BeneficiaryModel): void {
    const products: OpenProductName[] = SUPPORTED_BENEFICIARY_PRODUCTS
      .filter((product: OpenProductName) => !!getProductSubscriptionEntry(beneficiary.subscriptions, product));

    // Same priority rule as `UserService.getActiveSubscriptionEntry`: first supported product with a subscription wins, deterministically.
    const activeProduct: OpenProductName | undefined = products[0];

    if (!activeProduct) {
      // A beneficiary with no supported subscription is `/unauthorized` territory: cache nothing rather than cache a lie.
      this.forget();
      return;
    }

    const context: ProductContext = {
      uid: beneficiary.uid,
      products,
      activeProduct,
      updatedAt: Date.now(),
    };

    this.#localStorage.set('PRODUCT_CONTEXT', context);
    this.#resolution.set(context);
  }

  /** Called on logout, deliberately not purging `QR_CODE` / `QR_CODE_PRUNEO`. */
  forget(): void {
    this.#localStorage.remove('PRODUCT_CONTEXT');
    this.#resolution.set('unknown');
  }

  /** Synchronous, independent of the `onAuthStateChanged` hydration above: `authGuard` needs the answer in the same tick it learns the user, before that listener has fired. */
  readFor(uid: string): ProductResolution {
    const cached: ProductContext | null = this.#localStorage.get('PRODUCT_CONTEXT');

    if (!cached || cached.uid !== uid || !cached.activeProduct) {
      return 'unknown';
    }

    return cached;
  }
}
